import React, { useCallback, useEffect, useRef, useState } from "react";
import webui from "@tinqin/tinqin-web-ui";
import ResponsiveHOC from "../../HOC/components/responsive/index";

import "./additionalArticlesContainerStyles.css";
import { IComponentProps, IExtractedProps } from "Common/utils/commonTypes";

export interface IComponentResponsiveProps extends IComponentProps {
    size: string;
}

//component for showing "see more" button on Small screens
//once shown children remain visible
export const AdditionalArticlesContainer: React.FC<IComponentResponsiveProps> = props => {
    const [opened, setOpened] = useState<boolean>(false);
    const [hasButton, setHasButton] = useState<boolean>(false);
    const container: React.MutableRefObject<HTMLDivElement> = useRef();

    const onShowMore = useCallback((): void => {
        setOpened(true);
    }, [setOpened]);

    const smallScreen = useCallback((): boolean => {
        return props.size === "S" || props.size === "XS";
    }, [props.size]);

    const shouldRenderChildren = useCallback((): boolean => {
        return !smallScreen() || opened;
    }, [opened, smallScreen]);

    const getInnerChildren = useCallback((): HTMLCollection => {
        return container.current.children[0].children;
    }, [container]);
    //Show first article then 2 or 4 solution boxes if available
    const adjustChildrenSmallScreen = useCallback((): void => {
        let firstArticle;
        const solutionBoxes = [];
        if (!container.current) {
            return;
        }
        const children: HTMLCollection = getInnerChildren();

        let hasSolutions: boolean;
        for (let i = 0; i < children.length; ++i) {
            if (!hasSolutions && children[i].className.includes("solutionItem")) {
                hasSolutions = true;
            }
        }

        if (hasSolutions) {
            for (let i = 0; i < children.length; ++i) {
                if (!firstArticle && children[i].className.includes("articleItem")) {
                    firstArticle = children[i];
                } else if (children[i].className.includes("solutionItem")) {
                    solutionBoxes.push(children[i]);
                } else {
                    children[i].classList.add("tq-hide");
                }
            }
        } else {
            // show only 2 articles if there is more
            setHasButton(true);
            for (let i = 0; i < children.length; ++i) {
                if (i >= 2 && children[i].className.includes("articleItem")) {
                    children[i].classList.add("tq-hide");
                }
            }
        }

        //in case of 1 or 3 solution boxes, hide the last solution box
        //in case of more than 4, show only the first 4
        if (solutionBoxes.length === 1 || solutionBoxes.length === 3) {
            solutionBoxes[solutionBoxes.length - 1].classList.add("tq-hide");
        } else if (solutionBoxes.length > 4) {
            for (let j = 4; j < solutionBoxes.length; ++j) {
                solutionBoxes[j].classList.add("tq-hide");
            }
        }
    }, [getInnerChildren, setHasButton]);

    //removing any tq-hide classes
    const adjustChildrenBigScreen = useCallback((): void => {
        const children = getInnerChildren();
        for (let i = 0; i < children.length; ++i) {
            children[i].classList.remove("tq-hide");
            if (children[i].className.includes("seeMoreButton")) {
                children[i].classList.add("tq-hide");
            }
        }
    }, [getInnerChildren]);

    const adjustChildren = useCallback(() => {
        //in case of small screen display only some of the children
        if (smallScreen() && !opened) {
            adjustChildrenSmallScreen();
        } else {
            adjustChildrenBigScreen();
        }
    }, [adjustChildrenSmallScreen, adjustChildrenBigScreen, smallScreen, opened ]);

    useEffect(() => {
        adjustChildren();
    });

    // let data = this.props.data ? this.props.data.properties || {} : null;
    // if (!data) {
    //     return this.getEmptyContent();
    // }
    const dataProps: IExtractedProps = webui.getUtils().extractComponentProps(props, {
        additionalProps: ["label"]
    });
    const renderChildren: boolean = shouldRenderChildren();

    const containerClass: string = renderChildren ? "" : "tq-show-only-first-child";
    return (
        <div className={containerClass} ref={container}>
            {/* {this.resolveChildren()} */}
            {webui.getUtils().resolveChildren(props)}
            {hasButton && !renderChildren && (
                <div className="tq-flex seeMoreButton">
                    <button
                        onClick={onShowMore}
                        className="tq-button tq-button-border tq-offset-top-s un-button-border-blue"
                    >
                        <span className="tq-text-node">{dataProps.label}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default webui.connectComponent()(ResponsiveHOC(AdditionalArticlesContainer));
